/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

const createItem = (orderNumber = "") => ({
  orderNumber,
  assetNameAndSpecs: "",
  assetCode: "",
  assetCardNumber: "",
  bookOriginalCost: "",
  bookDepreciation: "",
  bookRemainingValue: "",
  revOriginalCost: "",
  revDepreciation: "",
  revRemainingValue: "",
  diffOriginalCost: "",
  diffDepreciation: "",
  diffRemainingValue: "",
});

const defaultItems = [createItem("1"), createItem("2"), createItem("3")];

function RevaluationRecordTable({ formData }) {
  const { values = {}, onChangeValue, setFieldValue } = formData || {};
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const today = new Date();

  const [headerInfo, setHeaderInfo] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    debitAccount: "",
    creditAccount: "",
    revDay: today.getDate().toString(),
    revMonth: (today.getMonth() + 1).toString(),
    revYear: today.getFullYear().toString(),
    decisionNumber: "",
    decisionDay: "",
    decisionMonth: "",
    decisionYear: "",
    decisionMaker: "",
    committee: [
      { name: "", position: "", representative: "", role: "Chủ tịch Hội đồng" },
      { name: "", position: "", representative: "", role: "Ủy viên" },
      { name: "", position: "", representative: "", role: "Ủy viên" },
    ],
    conclusion: "",
    creatorName: "",
    accountantName: "",
    headOfCommitteeName: "",
    signingDate: today.toISOString().split("T")[0],
  });

  const [localItems, setLocalItems] = useState(defaultItems);

  // Sync local state with formik values when loaded
  useEffect(() => {
    if (values.items && Array.isArray(values.items) && values.items.length > 0) {
      setLocalItems(values.items);
    }
  }, [values.items]);

  useEffect(() => {
    setHeaderInfo((prev) => ({
      ...prev,
      ...values,
    }));
  }, [values]);

  const handleHeaderChange = (field, val) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) {
      onChangeValue(field, val);
    }
    if (setFieldValue) {
      setFieldValue(field, val);
    }
  };

  const handleCommitteeChange = (index, field, val) => {
    const newCommittee = [...headerInfo.committee];
    newCommittee[index] = { ...newCommittee[index], [field]: val };
    setHeaderInfo((prev) => ({ ...prev, committee: newCommittee }));
    if (onChangeValue) {
      onChangeValue("committee", newCommittee);
    }
    if (setFieldValue) {
      setFieldValue("committee", newCommittee);
    }
  };

  const handleChangeItem = (index, field, value) => {
    const newItems = [...localItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // Auto calculate diff (Đánh giá lại - Ghi sổ)
    if (["bookOriginalCost", "revOriginalCost"].includes(field)) {
      const b = parseFloat(newItems[index].bookOriginalCost) || 0;
      const r = parseFloat(newItems[index].revOriginalCost) || 0;
      newItems[index].diffOriginalCost = r - b;
    }
    if (["bookDepreciation", "revDepreciation"].includes(field)) {
      const b = parseFloat(newItems[index].bookDepreciation) || 0;
      const r = parseFloat(newItems[index].revDepreciation) || 0;
      newItems[index].diffDepreciation = r - b;
    }
    if (["bookRemainingValue", "revRemainingValue"].includes(field)) {
      const b = parseFloat(newItems[index].bookRemainingValue) || 0;
      const r = parseFloat(newItems[index].revRemainingValue) || 0;
      newItems[index].diffRemainingValue = r - b;
    }

    setLocalItems(newItems);
  };

  const addRow = () => {
    const newItem = createItem((localItems.length + 1).toString());
    setLocalItems([...localItems, newItem]);
  };

  const removeRow = (index) => {
    if (localItems.length <= 1) return;
    const updated = localItems.filter((_, i) => i !== index);
    setLocalItems(updated);
  };

  const calculatedTotal = useMemo(() => {
    const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

    const fieldsToSum = [
      "bookOriginalCost",
      "bookDepreciation",
      "bookRemainingValue",
      "revOriginalCost",
      "revDepreciation",
      "revRemainingValue",
      "diffOriginalCost",
      "diffDepreciation",
      "diffRemainingValue",
    ];

    const total = {};
    fieldsToSum.forEach((field) => {
      total[field] = localItems.reduce((acc, item) => acc + num(item[field]), 0);
    });

    return total;
  }, [localItems]);

  useEffect(() => {
    if (onChangeValue) {
      onChangeValue("items", localItems);
      onChangeValue("total", calculatedTotal);
    }
  }, [localItems, calculatedTotal]);

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "10px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "40px",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "10px 0 20px 0", position: "relative" },
    titleMain: { fontSize: "18px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    rightInfoBox: { position: "absolute", right: 0, top: "20px", textAlign: "left" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "15px" },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: { border: "1px solid #000", padding: 0, height: "30px" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    totalText: {
      padding: "0 4px",
      textAlign: "right",
      fontWeight: "bold",
      display: "block",
      lineHeight: "30px",
    },
    totalRowBg: { backgroundColor: "#fff", fontWeight: "bold", textAlign: "center" },
    flexRow: { display: "flex", alignItems: "center", marginBottom: "8px" },
    removeBtn: {
      border: "none",
      background: "transparent",
      color: "red",
      fontWeight: "bold",
      cursor: "pointer",
      width: "100%",
      height: "100%",
    },
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "40%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.unit}
              onChange={(e) => handleHeaderChange("unit", e.target.value)}
            />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Bộ phận:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", fontWeight: "bold" }}
              value={headerInfo.department}
              onChange={(e) => handleHeaderChange("department", e.target.value)}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "60%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số 04 - TSCĐ</div>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
            Tài chính)
          </div>
        </div>
      </div>

      {/* TITLE & RIGHT INFO */}
      <div style={s.titleBox}>
        <h2 style={s.titleMain}>BIÊN BẢN ĐÁNH GIÁ LẠI TSCĐ</h2>
        <div style={{ fontStyle: "italic", marginTop: "5px" }}>
          Ngày
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, marginLeft: 4 }}
            value={headerInfo.revDay}
            onChange={(e) => handleHeaderChange("revDay", e.target.value)}
          />{" "}
          tháng
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, marginLeft: 4 }}
            value={headerInfo.revMonth}
            onChange={(e) => handleHeaderChange("revMonth", e.target.value)}
          />{" "}
          năm
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "60px", marginLeft: 4 }}
            value={headerInfo.revYear}
            onChange={(e) => handleHeaderChange("revYear", e.target.value)}
          />
          ...
        </div>

        <div style={s.rightInfoBox}>
          <div style={{ marginBottom: "4px" }}>
            Số:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "100px" }}
              value={headerInfo.documentNumber}
              onChange={(e) => handleHeaderChange("documentNumber", e.target.value)}
            />
          </div>
          <div style={{ marginBottom: "4px" }}>
            Nợ:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "100px" }}
              value={headerInfo.debitAccount}
              onChange={(e) => handleHeaderChange("debitAccount", e.target.value)}
            />
          </div>
          <div>
            Có:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "100px" }}
              value={headerInfo.creditAccount}
              onChange={(e) => handleHeaderChange("creditAccount", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* THÔNG TIN CĂN CỨ VÀ HỘI ĐỒNG */}
      <div style={{ marginBottom: "10px", marginTop: "40px" }}>
        <div style={{ marginBottom: "8px" }}>
          Căn cứ Quyết định số:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px" }}
            value={headerInfo.decisionNumber}
            onChange={(e) => handleHeaderChange("decisionNumber", e.target.value)}
          />{" "}
          ngày
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, marginLeft: 4 }}
            value={headerInfo.decisionDay}
            onChange={(e) => handleHeaderChange("decisionDay", e.target.value)}
          />{" "}
          tháng
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, marginLeft: 4 }}
            value={headerInfo.decisionMonth}
            onChange={(e) => handleHeaderChange("decisionMonth", e.target.value)}
          />{" "}
          năm
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "60px", marginLeft: 4 }}
            value={headerInfo.decisionYear}
            onChange={(e) => handleHeaderChange("decisionYear", e.target.value)}
          />{" "}
          ...
        </div>
        <div style={{ marginBottom: "8px" }}>
          Của{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "250px" }}
            value={headerInfo.decisionMaker}
            onChange={(e) => handleHeaderChange("decisionMaker", e.target.value)}
          />{" "}
          Về việc đánh giá lại{" "}
          <span style={{ textDecoration: "underline", fontWeight: "bold" }}>TSCĐ</span>
        </div>

        {headerInfo.committee.map((member, index) => (
          <div key={index} style={s.flexRow}>
            - Ông/Bà:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "200px", margin: "0 10px" }}
              value={member.name}
              onChange={(e) => handleCommitteeChange(index, "name", e.target.value)}
            />
            Chức vụ{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "150px", margin: "0 10px" }}
              value={member.position}
              onChange={(e) => handleCommitteeChange(index, "position", e.target.value)}
            />
            Đại diện{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "150px", margin: "0 10px" }}
              value={member.representative}
              onChange={(e) => handleCommitteeChange(index, "representative", e.target.value)}
            />
            {member.role}
          </div>
        ))}
        <div style={{ marginTop: "15px", fontWeight: "bold" }}>
          Đã thực hiện đánh giá lại giá trị các TSCĐ sau đây:
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ ...s.th, width: "40px" }}>
                STT
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "160px" }}>
                Tên, ký mã
                <br />
                hiệu, qui
                <br />
                cách (cấp
                <br />
                hạng) TSCĐ
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>
                Số hiệu
                <br />
                TSCĐ
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>
                Số thẻ
                <br />
                TSCĐ
              </th>
              <th colSpan={3} style={s.th}>
                Giá trị đang ghi sổ
              </th>
              <th colSpan={3} style={s.th}>
                Giá trị còn lại theo đánh
                <br />
                giá lại
              </th>
              <th colSpan={3} style={s.th}>
                Chênh lệch
              </th>
              {!containView && (
                <th rowSpan={3} style={{ ...s.th, width: "30px" }}>
                  #
                </th>
              )}
            </tr>
            <tr>
              <th style={{ ...s.th, width: "80px" }}>
                Nguyên
                <br />
                Giá
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Hao
                <br />
                mòn
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Giá trị
                <br />
                còn lại
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Nguyên
                <br />
                Giá
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Hao
                <br />
                mòn
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Giá trị
                <br />
                còn lại
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Nguyên
                <br />
                giá
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Hao
                <br />
                mòn
              </th>
              <th style={{ ...s.th, width: "80px" }}>
                Giá trị
                <br />
                Còn lại
              </th>
            </tr>
            <tr style={{ backgroundColor: "#fafafa" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              <th style={s.th}>7</th>
              <th style={s.th}>8</th>
              <th style={s.th}>9</th>
            </tr>
          </thead>
          <tbody>
            {localItems.map((row, i) => (
              <tr key={i}>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.orderNumber || ""}
                    onChange={(e) => handleChangeItem(i, "orderNumber", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableLeft}
                    value={row.assetNameAndSpecs || ""}
                    onChange={(e) => handleChangeItem(i, "assetNameAndSpecs", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.assetCode || ""}
                    onChange={(e) => handleChangeItem(i, "assetCode", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.assetCardNumber || ""}
                    onChange={(e) => handleChangeItem(i, "assetCardNumber", e.target.value)}
                  />
                </td>

                {/* Data columns 1 to 9 */}
                {[
                  "bookOriginalCost",
                  "bookDepreciation",
                  "bookRemainingValue",
                  "revOriginalCost",
                  "revDepreciation",
                  "revRemainingValue",
                  "diffOriginalCost",
                  "diffDepreciation",
                  "diffRemainingValue",
                ].map((field) => (
                  <td key={field} style={s.td}>
                    <input
                      disabled={containView}
                      type="number"
                      style={s.inputTable}
                      value={row[field] || ""}
                      onChange={(e) => handleChangeItem(i, field, e.target.value)}
                    />
                  </td>
                ))}

                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(i)}>
                      X
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {/* Total Row */}
            <tr style={s.totalRowBg}>
              <td style={s.td}></td>
              <td style={s.td}>Cộng</td>
              <td style={s.td}>X</td>
              <td style={s.td}>X</td>

              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.bookOriginalCost)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.bookDepreciation)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.bookRemainingValue)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.revOriginalCost)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.revDepreciation)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.revRemainingValue)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.diffOriginalCost)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.diffDepreciation)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.diffRemainingValue)}</span>
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>
        {!containView && (
          <SoftBox mt={4} display="flex" justifyContent="flex-end">
            <SoftButton variant="outlined" color="dark" onClick={addRow}>
              THÊM DÒNG
            </SoftButton>
          </SoftBox>
        )}
      </div>

      {/* KẾT LUẬN */}
      <div style={{ marginTop: "20px", fontWeight: "bold" }}>Kết luận:</div>
      <div>
        <textarea
          disabled={containView}
          style={{
            width: "100%",
            minHeight: "60px",
            border: "none",
            borderBottom: "1px dotted #000",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: "14px",
            outline: "none",
            resize: "vertical",
            lineHeight: "1.5",
          }}
          value={headerInfo.conclusion}
          onChange={(e) => handleHeaderChange("conclusion", e.target.value)}
          placeholder="...................................................................................................................................................."
        />
      </div>

      {/* FOOTER - Chỉ hiện khi VIEW */}
      {containView && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "40px",
            textAlign: "center",
          }}
        >
          <div style={{ width: "33%" }}>
            <b>Ủy viên/người lập</b>
            <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "65px" }}>
              (Ký, họ tên)
            </div>
            <input
              style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
              value={headerInfo.creatorName}
              readOnly
            />
          </div>
          <div style={{ width: "33%" }}>
            <b>Kế toán trưởng</b>
            <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "65px" }}>
              (Ký, họ tên)
            </div>
            <input
              style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
              value={headerInfo.accountantName}
              readOnly
            />
          </div>
          <div style={{ width: "33%" }}>
            <div style={{ fontStyle: "italic", marginBottom: 5 }}>
              Ngày {headerInfo.signingDate ? new Date(headerInfo.signingDate).getDate() : "..."}{" "}
              tháng{" "}
              {headerInfo.signingDate ? new Date(headerInfo.signingDate).getMonth() + 1 : "..."} năm{" "}
              {headerInfo.signingDate ? new Date(headerInfo.signingDate).getFullYear() : "..."}
            </div>
            <b>Chủ tịch Hội đồng</b>
            <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "45px" }}>
              (Ký, họ tên)
            </div>
            <input
              style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
              value={headerInfo.headOfCommitteeName}
              readOnly
            />
          </div>
        </div>
      )}

      {/* GHI CHÚ */}
      <div style={{ marginTop: "40px", fontSize: "14px", paddingBottom: "20px" }}>
        <b>Ghi chú:</b> <i style={{ textDecoration: "underline" }}>Tùy</i>{" "}
        <i style={{ fontStyle: "italic" }}>
          theo đặc điểm hoạt động sản xuất kinh doanh và yêu cầu quản lý của đơn vị mình, doanh
          nghiệp được xây dựng, thiết kế biểu mẫu chứng từ kế toán.
        </i>
      </div>
    </SoftBox>
  );
}

RevaluationRecordTable.propTypes = {
  formData: PropTypes.object,
};

export default RevaluationRecordTable;
